import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useAuth from 'auth/useAuth';
import PricingSection from 'sections/auth/PricingSection/PricingSection';
import { useInitiateSubscription } from 'APIs/Subscription/subscription';
import { handlePaymentGateway } from 'utils/paymentGateway';
import { notify } from '@common/NotificationService';

export default function SubscriptionWall() {
  const { user, meLoading, refreshMe } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const { initiateSubscription } = useInitiateSubscription();

  const isInactive =
    !meLoading &&
    user?.user?.user_type === 'merchant_admin' &&
    user?.subscription?.status !== 'active';

  // Blur only the content area — header/sidebar stay fully interactive
  useEffect(() => {
    const el = document.querySelector('.pc-container');
    if (!el) return;
    el.classList.toggle('sw-content-blurred', isInactive);
    return () => el.classList.remove('sw-content-blurred');
  }, [isInactive]);

  if (!isInactive) return null;

  const handlePlanSelect = async (plan) => {
    try {
      const payInfo = await initiateSubscription({
        merchant_id: user.user.id,
        plan_id: plan.planId,
        billing_cycle: 'monthly',
        source: 'reactivation'
      });

      await handlePaymentGateway(payInfo, {
        onRazorpaySuccess: async (_, d) => {
          setShowModal(false);
          await refreshMe();
          navigate(`/payment/result/${d.txnid}`);
        },
        onRazorpayCancel: () => notify.error('Payment cancelled.'),
        onRazorpayFail: (msg) => notify.error(msg || 'Payment failed.'),
        onSdkError: () => notify.error('Failed to load payment gateway. Please try again.')
      });
    } catch (e) {
      notify.error(e?.response?.data?.message || 'Error initiating payment. Please try again.');
    }
  };

  const isTrialExpired =
    user?.subscription?.billing_cycle === 'trial' &&
    user?.subscription?.status === 'expired';

  const hasNoSubscription = !user?.subscription;

  const title = isTrialExpired
    ? 'Your 30-day free trial has ended'
    : hasNoSubscription
    ? 'Complete your setup'
    : 'Subscription Inactive';

  const desc = isTrialExpired
    ? 'Your free trial has expired. Choose a plan below to continue using SeatUp.'
    : hasNoSubscription
    ? 'Select a plan to activate your account and unlock all features.'
    : 'Your subscription has expired or is not active. Renew to unlock all features.';

  const btnLabel = isTrialExpired || hasNoSubscription ? 'Choose a Plan' : 'Renew Subscription';

  return (
    <>
      {/* pointer-events: none so clicks pass through to header/sidebar */}
      <div className="sw-overlay">
        <div className="sw-card">
          <i className="ph ph-lock-key sw-lock-icon" />
          <h5 className="sw-title">{title}</h5>
          <p className="sw-desc">{desc}</p>
          <button className="sw-btn btn btn-primary" onClick={() => setShowModal(true)}>
            {btnLabel}
          </button>
        </div>
      </div>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        centered
        contentClassName="sw-modal-content"
      >
        <Modal.Header
          closeButton
          style={{
            background: '#1677ff',
            borderBottom: 'none',
            borderRadius: '14px 14px 0 0',
            padding: '22px 28px'
          }}
        >
          <div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 800, fontSize: 11, letterSpacing: 3, marginBottom: 4 }}>
              SEATUP
            </div>
            <Modal.Title style={{ color: '#fff', fontWeight: 800, fontSize: 20, margin: 0 }}>
              {isTrialExpired ? 'Your trial has ended' : 'Activate your subscription'}
            </Modal.Title>
            <p style={{ color: 'rgba(255,255,255,0.75)', margin: '5px 0 0', fontSize: 13 }}>
              {isTrialExpired
                ? 'Your 30-day free trial is over. Choose a plan to continue.'
                : 'Choose a plan below to unlock your account.'}
            </p>
          </div>
        </Modal.Header>

        <Modal.Body className="sw-modal-body">
          <div className="sw-pricing">
            <PricingSection
              onSelectPlan={handlePlanSelect}
              currentPlanId={user?.subscription?.plan_id}
              renewMode
            />
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
